@file:OptIn(ExperimentalMaterial3Api::class, ExperimentalFoundationApi::class, ExperimentalLayoutApi::class)

package com.example.monetix.ui.dialogs

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.core.animateDpAsState
import androidx.compose.animation.core.tween
import androidx.compose.ui.draw.blur
import androidx.compose.ui.graphics.Brush
import androidx.compose.animation.expandVertically
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.shrinkVertically
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.gestures.snapping.rememberSnapFlingBehavior
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.ExperimentalFoundationApi
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ArrowBack
import androidx.compose.material.icons.filled.ArrowDownward
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.filled.KeyboardArrowDown
import androidx.compose.material.icons.filled.KeyboardArrowUp
import androidx.compose.material.icons.filled.MoreVert
import androidx.compose.material.icons.filled.Search
import androidx.compose.material.icons.outlined.Delete
import androidx.compose.material.icons.outlined.Search
import androidx.compose.material3.*
import com.example.monetix.ui.components.MonetixTextField
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsFocusedAsState
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import androidx.compose.ui.window.PopupProperties
import coil3.compose.AsyncImage
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.todayIn
import app.cash.sqldelight.async.coroutines.awaitAsList
import kotlin.random.Random

import com.example.monetix.db.MonetixDatabase
import com.example.monetix.db.AccountEntity
import com.example.monetix.db.CategoryEntity
import com.example.monetix.db.RecurringExpenseEntity
import com.example.monetix.stripSystemMarker
import com.example.monetix.ui.components.MonetixGlassSwitch
import com.example.monetix.ui.components.TxLogo
import com.example.monetix.utils.rememberMediaHandler
import com.example.monetix.utils.parseEuroToCents
import com.example.monetix.utils.autoFaviconUrlForName
import kotlin.math.abs

// ==========================================
// 🚀 IL MOTORE SEMANTICO PER GLI ABBONAMENTI
// ==========================================
private data class PredefSubscription(
    val name: String,
    val suggestedCategory: String,
    val domain: String? = null,
    val emoji: String? = null,
    val customUrl: String? = null
) {
    val url get() = customUrl ?: domain?.let { "https://icon.horse/icon/$it" }
}

private val defaultSubscriptionSuggestions = listOf(
    PredefSubscription("Netflix", "RISTORANTI E SVAGO", "netflix.com"),
    PredefSubscription("Spotify", "RISTORANTI E SVAGO", emoji = "🎵"),
    PredefSubscription("Amazon Prime", "SHOPPING", "amazon.it"),
    PredefSubscription("Disney+", "RISTORANTI E SVAGO", "disneyplus.com"),
    PredefSubscription("DAZN", "RISTORANTI E SVAGO", "dazn.com"),
    PredefSubscription("YouTube Premium", "RISTORANTI E SVAGO", "youtube.com"),
    PredefSubscription("Iliad", "CASA E BOLLETTE", "iliad.it"),
    PredefSubscription("Vodafone", "CASA E BOLLETTE", "vodafone.it"),
    PredefSubscription("TIM", "CASA E BOLLETTE", "tim.it"),
    PredefSubscription("Fastweb", "CASA E BOLLETTE", "fastweb.it"),
    PredefSubscription("Enel Energia", "CASA E BOLLETTE", "enel.it", "⚡"),
    PredefSubscription("Eni Plenitude", "CASA E BOLLETTE", "eni.com", "⚡"),
    PredefSubscription("Palestra", "SALUTE", emoji = "🏋️"),
    PredefSubscription("Affitto", "CASA E BOLLETTE", emoji = "🏘️"),
    PredefSubscription("Mutuo", "CASA E BOLLETTE", emoji = "🏦"),
    PredefSubscription("Assicurazione Auto", "AUTO E TRASPORTI", emoji = "🛡️")
)

// ==========================================
// 🚀 COMPONENTI ISOLATI E PRIVATI
// ==========================================

@Composable
private fun StableWebTextField(
    value: String,
    onValueChange: (String) -> Unit,
    label: String,
    modifier: Modifier = Modifier,
    trailingIcon: @Composable (() -> Unit)? = null,
    leadingIcon: @Composable (() -> Unit)? = null,
    enabled: Boolean = true,
    interactionSource: MutableInteractionSource = remember { MutableInteractionSource() },
    keyboardType: KeyboardType = KeyboardType.Text
) {
    MonetixTextField(
        value = value,
        onValueChange = onValueChange,
        label = { Text(label, style = MaterialTheme.typography.labelSmall, color = Color(0xFFA0A0A0)) },
        maxLines = 1,
        enabled = enabled,
        modifier = modifier.fillMaxWidth(),
        shape = RoundedCornerShape(14.dp),
        trailingIcon = trailingIcon,
        leadingIcon = leadingIcon,
        interactionSource = interactionSource,
        keyboardOptions = KeyboardOptions(
            keyboardType = keyboardType,
            autoCorrectEnabled = false
        )
    )
}

@Composable
private fun StableWebNumberField(
    value: String,
    onValueChange: (String) -> Unit,
    label: String,
    modifier: Modifier = Modifier,
    trailingIcon: @Composable (() -> Unit)? = null,
    leadingIcon: @Composable (() -> Unit)? = null,
    enabled: Boolean = true,
    interactionSource: MutableInteractionSource = remember { MutableInteractionSource() }
) {
    MonetixTextField(
        value = value,
        onValueChange = { newVal ->
            if (newVal.all { c -> c.isDigit() || c == ',' || c == '.' || c == ' ' }) {
                onValueChange(newVal)
            }
        },
        label = { Text(label, style = MaterialTheme.typography.labelSmall, color = Color(0xFFA0A0A0)) },
        maxLines = 1,
        enabled = enabled,
        modifier = modifier.fillMaxWidth(),
        shape = RoundedCornerShape(14.dp),
        trailingIcon = trailingIcon,
        leadingIcon = leadingIcon,
        interactionSource = interactionSource,
        keyboardOptions = KeyboardOptions(
            keyboardType = KeyboardType.Decimal,
            autoCorrectEnabled = false
        )
    )
}

@Composable
private fun PanelBox(content: @Composable ColumnScope.() -> Unit) {
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(16.dp),
        color = Color.White.copy(alpha = 0.08f),
        border = BorderStroke(1.dp, Color.White.copy(alpha = 0.15f))
    ) {
        Column(
            modifier = Modifier.padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            content()
        }
    }
}

@OptIn(ExperimentalFoundationApi::class)
@Composable
private fun AmountWheelDialog(
    initialAmount: String,
    onDismiss: () -> Unit,
    onConfirm: (String) -> Unit
) {
    val cleanInitial = initialAmount.replace(",", ".").ifBlank { "0.00" }
    val parts = cleanInitial.split(".")
    val initEuro = parts.getOrNull(0)?.toIntOrNull() ?: 0
    val initCents = parts.getOrNull(1)?.padEnd(2, '0')?.take(2)?.toIntOrNull() ?: 0

    val euros = (0..9999).toList()
    val cents = (0..99).toList()

    val euroState = rememberLazyListState(initialFirstVisibleItemIndex = initEuro)
    val centState = rememberLazyListState(initialFirstVisibleItemIndex = initCents)

    AlertDialog(
        onDismissRequest = onDismiss,
        containerColor = Color(0xFF1C1C1E),
        title = { Text("Seleziona Importo", style = MaterialTheme.typography.titleMedium, color = Color.White) },
        text = {
            Row(
                modifier = Modifier.fillMaxWidth().height(180.dp),
                horizontalArrangement = Arrangement.Center,
                verticalAlignment = Alignment.CenterVertically
            ) {
                LazyColumn(
                    state = euroState,
                    flingBehavior = rememberSnapFlingBehavior(euroState),
                    modifier = Modifier.weight(1f).height(150.dp),
                    horizontalAlignment = Alignment.CenterHorizontally,
                    contentPadding = PaddingValues(vertical = 55.dp)
                ) {
                    items(euros.size) { i ->
                        val isSelected by remember { derivedStateOf { euroState.firstVisibleItemIndex == i } }
                        Text(
                            text = "${euros[i]}",
                            fontSize = if (isSelected) 28.sp else 18.sp,
                            fontWeight = if (isSelected) FontWeight.Bold else FontWeight.Normal,
                            color = if (isSelected) MaterialTheme.colorScheme.primary else Color(0xFFA0A0A0),
                            modifier = Modifier.padding(vertical = 6.dp)
                        )
                    }
                }

                Text(",", fontSize = 32.sp, fontWeight = FontWeight.Bold, color = Color.White, modifier = Modifier.padding(horizontal = 8.dp))

                LazyColumn(
                    state = centState,
                    flingBehavior = rememberSnapFlingBehavior(centState),
                    modifier = Modifier.weight(1f).height(150.dp),
                    horizontalAlignment = Alignment.CenterHorizontally,
                    contentPadding = PaddingValues(vertical = 55.dp)
                ) {
                    items(cents.size) { i ->
                        val isSelected by remember { derivedStateOf { centState.firstVisibleItemIndex == i } }
                        Text(
                            text = cents[i].toString().padStart(2, '0'),
                            fontSize = if (isSelected) 28.sp else 18.sp,
                            fontWeight = if (isSelected) FontWeight.Bold else FontWeight.Normal,
                            color = if (isSelected) MaterialTheme.colorScheme.primary else Color(0xFFA0A0A0),
                            modifier = Modifier.padding(vertical = 6.dp)
                        )
                    }
                }
            }
        },
        confirmButton = {
            Button(onClick = {
                val e = euros[euroState.firstVisibleItemIndex]
                val c = cents[centState.firstVisibleItemIndex].toString().padStart(2, '0')
                onConfirm("$e,$c")
            }) { Text("CONFERMA") }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) { Text("ANNULLA", color = Color(0xFFA0A0A0)) }
        }
    )
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun WebImagePickerPopup(
    initialQuery: String,
    onDismiss: () -> Unit,
    onImageSelected: (String) -> Unit
) {
    var query by remember { mutableStateOf(initialQuery) }
    var results by remember { mutableStateOf<List<String>>(emptyList()) }
    val uriHandler = LocalUriHandler.current

    val popularBrands = remember {
        listOf("Amazon", "Netflix", "Spotify", "PayPal", "Iliad", "Postepay", "Enel", "Trenitalia", "Conad", "Coop", "McDonald's", "Apple", "Uber", "Deliveroo")
    }

    fun generateLinks(qRaw: String) {
        val q = qRaw.trim().lowercase().replace(" ", "")
        if (q.isEmpty()) {
            results = emptyList()
            return
        }
        val domain = if (q.contains(".")) q else "$q.com"
        val domainIt = if (q.contains(".")) q else "$q.it"

        results = listOf(
            "https://unavatar.io/$domain",
            "https://unavatar.io/$domainIt",
            "https://icon.horse/icon/$domain",
            "https://icon.horse/icon/$domainIt",
            "https://www.google.com/s2/favicons?domain=$domain&sz=128",
            "https://www.google.com/s2/favicons?domain=$domainIt&sz=128"
        ).distinct()
    }

    LaunchedEffect(initialQuery) {
        if (initialQuery.isNotBlank()) generateLinks(initialQuery)
    }

    AlertDialog(
        onDismissRequest = onDismiss,
        shape = RoundedCornerShape(28.dp),
        containerColor = Color(0xFF1E1B4B),
        title = {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Surface(
                    shape = CircleShape,
                    color = Color(0xFF60A5FA).copy(alpha = 0.2f),
                    modifier = Modifier.size(36.dp)
                ) {
                    Icon(
                        imageVector = Icons.Default.Search,
                        contentDescription = null,
                        tint = Color(0xFF60A5FA),
                        modifier = Modifier.padding(8.dp)
                    )
                }
                Spacer(Modifier.width(12.dp))
                Column {
                    Text(
                        "Ricerca Logo Web 🌐",
                        style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.Bold),
                        color = Color.White
                    )
                    Text(
                        "Trova il logo ufficiale del merchant",
                        style = MaterialTheme.typography.labelSmall,
                        color = Color.White.copy(alpha = 0.6f)
                    )
                }
            }
        },
        text = {
            Column(
                modifier = Modifier.fillMaxWidth().verticalScroll(rememberScrollState()),
                verticalArrangement = Arrangement.spacedBy(14.dp)
            ) {
                MonetixTextField(
                    value = query,
                    onValueChange = {
                        query = it
                        generateLinks(it)
                    },
                    label = { Text("Brand o Nome Azienda", color = Color(0xFFA0A0A0)) },
                    placeholder = { Text("es. Netflix, Amazon, Iliad...", color = Color(0xFFA0A0A0)) },
                    trailingIcon = {
                        if (query.isNotBlank()) {
                            IconButton(onClick = { query = ""; results = emptyList() }) {
                                Icon(Icons.Default.Close, contentDescription = "Cancella", tint = Color.White.copy(alpha = 0.6f))
                            }
                        } else {
                            Icon(Icons.Default.Search, contentDescription = null, tint = Color(0xFF60A5FA))
                        }
                    },
                    singleLine = true,
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(14.dp),
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedTextColor = Color.White,
                        unfocusedTextColor = Color.White,
                        focusedContainerColor = Color.Black.copy(alpha = 0.35f),
                        unfocusedContainerColor = Color.Black.copy(alpha = 0.25f),
                        focusedBorderColor = Color(0xFF60A5FA),
                        unfocusedBorderColor = Color.White.copy(alpha = 0.20f)
                    )
                )

                Text("Brand frequenti:", style = MaterialTheme.typography.labelSmall, color = Color.White.copy(alpha = 0.7f))
                LazyRow(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    items(popularBrands) { brand ->
                        FilterChip(
                            selected = query.equals(brand, ignoreCase = true),
                            onClick = { query = brand; generateLinks(brand) },
                            label = { Text(brand, fontSize = 11.sp, fontWeight = FontWeight.SemiBold) },
                            colors = FilterChipDefaults.filterChipColors(
                                selectedContainerColor = Color(0xFF60A5FA),
                                selectedLabelColor = Color.White,
                                containerColor = Color.White.copy(alpha = 0.08f),
                                labelColor = Color.White.copy(alpha = 0.8f)
                            ),
                            border = null
                        )
                    }
                }

                if (results.isNotEmpty()) {
                    Text("Tocca il logo da usare:", style = MaterialTheme.typography.labelSmall, color = Color.White.copy(alpha = 0.7f))
                    LazyVerticalGrid(
                        columns = GridCells.Adaptive(64.dp),
                        horizontalArrangement = Arrangement.spacedBy(10.dp),
                        verticalArrangement = Arrangement.spacedBy(10.dp),
                        modifier = Modifier.height(160.dp).fillMaxWidth()
                    ) {
                        items(results) { url ->
                            Surface(
                                modifier = Modifier
                                    .aspectRatio(1f)
                                    .clickable { onImageSelected(url) },
                                shape = RoundedCornerShape(16.dp),
                                color = Color.White.copy(alpha = 0.15f),
                                border = BorderStroke(1.dp, Color.White.copy(alpha = 0.25f))
                            ) {
                                AsyncImage(
                                    model = url,
                                    contentDescription = null,
                                    modifier = Modifier.fillMaxSize().padding(10.dp),
                                    contentScale = ContentScale.Fit
                                )
                            }
                        }
                    }
                } else {
                    Box(modifier = Modifier.height(80.dp).fillMaxWidth(), contentAlignment = Alignment.Center) {
                        Text("Digita il nome di un brand per caricare i loghi", style = MaterialTheme.typography.bodySmall, color = Color(0xFFA0A0A0))
                    }
                }

                HorizontalDivider(color = Color.White.copy(alpha = 0.10f))

                Button(
                    onClick = {
                        val q = if (query.isNotBlank()) "${query} logo icon" else "logo icon"
                        uriHandler.openUri("https://www.google.com/search?tbm=isch&q=${q.replace(" ", "+")}")
                    },
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(12.dp),
                    colors = ButtonDefaults.buttonColors(containerColor = Color.White.copy(alpha = 0.12f), contentColor = Color.White)
                ) {
                    Icon(Icons.Outlined.Search, contentDescription = null, modifier = Modifier.size(18.dp), tint = Color(0xFF60A5FA))
                    Spacer(Modifier.width(8.dp))
                    Text("Cerca immagini su Google ↗", style = MaterialTheme.typography.labelMedium)
                }

                var manualUrl by remember { mutableStateOf("") }
                MonetixTextField(
                    value = manualUrl,
                    onValueChange = { manualUrl = it },
                    label = { Text("O incolla URL immagine", color = Color(0xFFA0A0A0)) },
                    placeholder = { Text("https://...", color = Color(0xFFA0A0A0)) },
                    singleLine = true,
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(14.dp),
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedTextColor = Color.White,
                        unfocusedTextColor = Color.White,
                        focusedContainerColor = Color.Black.copy(alpha = 0.35f),
                        unfocusedContainerColor = Color.Black.copy(alpha = 0.25f),
                        focusedBorderColor = Color(0xFF60A5FA),
                        unfocusedBorderColor = Color.White.copy(alpha = 0.20f)
                    )
                )

                AnimatedVisibility(visible = manualUrl.isNotBlank()) {
                    Column(
                        modifier = Modifier.fillMaxWidth().padding(top = 4.dp),
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        Surface(
                            modifier = Modifier.size(64.dp),
                            shape = RoundedCornerShape(16.dp),
                            color = Color.White.copy(alpha = 0.15f),
                            border = BorderStroke(1.dp, Color.White.copy(alpha = 0.25f))
                        ) {
                            AsyncImage(
                                model = manualUrl.trim(),
                                contentDescription = "Anteprima URL",
                                modifier = Modifier.fillMaxSize().padding(8.dp),
                                contentScale = ContentScale.Fit
                            )
                        }
                        Spacer(Modifier.height(10.dp))
                        Button(
                            onClick = { onImageSelected(manualUrl.trim()) },
                            shape = RoundedCornerShape(12.dp),
                            colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF60A5FA), contentColor = Color.White)
                        ) {
                            Text("Usa questa immagine", fontWeight = FontWeight.Bold)
                        }
                    }
                }
            }
        },
        confirmButton = {},
        dismissButton = {
            TextButton(onClick = onDismiss) {
                Text("ANNULLA", color = Color.White.copy(alpha = 0.6f), fontWeight = FontWeight.Bold)
            }
        }
    )
}

// ==========================================
// 🚀 DIALOG PRINCIPALE
// ==========================================

@Composable
private fun RowScope.TabBox(text: String, isSelected: Boolean, activeColor: Color, onClick: () -> Unit) {
    Box(
        modifier = Modifier.weight(1f)
            .clickable(onClick = onClick)
            .background(if (isSelected) activeColor else Color.Transparent)
            .padding(vertical = 12.dp),
        contentAlignment = Alignment.Center
    ) {
        Text(
            text = text,
            color = if (isSelected) Color.White else Color(0xFFA0A0A0),
            fontWeight = if (isSelected) FontWeight.Bold else FontWeight.Medium,
            style = MaterialTheme.typography.labelSmall
        )
    }
}

@OptIn(ExperimentalMaterial3Api::class, ExperimentalLayoutApi::class)
@Composable
fun RecurringExpenseDialog(
    db: MonetixDatabase,
    initial: RecurringExpenseEntity? = null,
    categories: List<CategoryEntity>,
    accounts: List<AccountEntity>,
    onDismiss: () -> Unit,
    onSave: (RecurringExpenseEntity) -> Unit,
    preferredAccountId: String?,
    onCategoryCreate: ((CategoryEntity) -> Unit)? = null
) {
    val brandPrimary = MaterialTheme.colorScheme.primary
    val today = Clock.System.todayIn(TimeZone.currentSystemDefault())
    val nowMs = Clock.System.now().toEpochMilliseconds()
    val scope = rememberCoroutineScope()

    var existingExpenses by remember { mutableStateOf<List<RecurringExpenseEntity>>(emptyList()) }
    LaunchedEffect(Unit) {
        try { existingExpenses = db.recurringExpenseQueries.all().awaitAsList() } catch(e: Exception) {}
    }

    var selectedType by remember(initial?.id) { mutableStateOf(initial?.type ?: "EXPENSE") }

    val isTransferMode = initial?.toAccountId != null || initial?.type == "TRANSFER"
    var isTransfer by remember(initial?.id) { mutableStateOf(isTransferMode) }

    var nameText by remember(initial?.id) { mutableStateOf(initial?.name ?: "") }
    val nameInteractionSource = remember { MutableInteractionSource() }
    val isNameFocused by nameInteractionSource.collectIsFocusedAsState()

    var notesText by remember(initial?.id) { mutableStateOf(initial?.notes ?: "") }

    val initialAmtStr = initial?.let {
        val e = it.amountCents / 100
        val c = abs(it.amountCents % 100).toString().padStart(2, '0')
        "$e,$c"
    } ?: ""
    var amountText by remember(initial?.id) { mutableStateOf(initialAmtStr) }
    val amountInteractionSource = remember { MutableInteractionSource() }
    var showAmountPicker by remember { mutableStateOf(false) }

    var dayOfMonthText by remember(initial?.id) { mutableStateOf(initial?.dayOfMonth?.toString() ?: today.dayOfMonth.toString()) }
    val dayInteractionSource = remember { MutableInteractionSource() }

    var cycleTargetText by remember(initial?.id) { mutableStateOf(initial?.cycleTarget?.toString() ?: "") }
    val cycleInteractionSource = remember { MutableInteractionSource() }

    val isInitialCustom = initial?.frequency?.startsWith("CUSTOM_") == true
    val initialCustomDays = if (isInitialCustom) initial?.frequency?.split("_")?.getOrNull(1) ?: "" else ""
    val initialSkipWeekend = if (isInitialCustom) initial?.frequency?.split("_")?.getOrNull(2) != "NOSKIP" else true

    var frequencyType by remember(initial?.id) { mutableStateOf(if (isInitialCustom) "Personalizzato" else initial?.frequency ?: "Mensile") }
    var customDaysText by remember(initial?.id) { mutableStateOf(initialCustomDays) }
    var skipWeekends by remember(initial?.id) { mutableStateOf(initialSkipWeekend) }

    val savedNextDateStr = remember(initial?.nextEpochDay) {
        initial?.let {
            val d = LocalDate.fromEpochDays(it.nextEpochDay.toInt())
            "${d.dayOfMonth.toString().padStart(2, '0')}/${d.monthNumber.toString().padStart(2, '0')}/${d.year}"
        }
    }
    var autoGenerate by remember(initial?.id) { mutableStateOf(initial?.autoGenerateOnReminder ?: true) }

    var toAccountId by remember(initial?.id) { mutableStateOf(initial?.toAccountId) }
    var toAccountExpanded by remember { mutableStateOf(false) }
    var accountId by remember(initial?.id) { mutableStateOf(initial?.accountId ?: preferredAccountId ?: accounts.firstOrNull { !it.isArchived && it.isVisible && !it.isDeleted }?.id ?: accounts.firstOrNull { !it.isDeleted }?.id) }
    var accountExpanded by remember { mutableStateOf(false) }
    var showHiddenAccounts by remember { mutableStateOf(false) }

    // --- 🚀 STATI LOGHI E CONTROLLI ---
    var logoEmoji by remember(initial?.id) { mutableStateOf(initial?.logoEmoji ?: "") }
    var logoUri by remember(initial?.id) { mutableStateOf(initial?.logoUri ?: "") }
    var manualLogoSet by remember(initial?.id) { mutableStateOf(!initial?.logoUri.isNullOrBlank() || !initial?.logoEmoji.isNullOrBlank()) }

    var showEmojiInput by remember { mutableStateOf(false) }
    var showWebSearch by remember { mutableStateOf(false) }

    var pendingSaveEntity by remember { mutableStateOf<RecurringExpenseEntity?>(null) }

    val initialCategoryName = categories.firstOrNull { it.id == initial?.categoryId }?.name ?: ""
    var categorySearchText by remember(initial?.id) { mutableStateOf(initialCategoryName) }
    val catInteractionSource = remember { MutableInteractionSource() }
    val isCatFocused by catInteractionSource.collectIsFocusedAsState()
    var categoryMenuOpen by remember { mutableStateOf(false) }

    var merchantSuggestionsExpanded by remember { mutableStateOf(false) }

    LaunchedEffect(isCatFocused) {
        if (isCatFocused) {
            if (categorySearchText == "NESSUNA CATEGORIA") categorySearchText = ""
            categoryMenuOpen = true
        }
    }

    val mediaHandler = rememberMediaHandler(
        onImageResult = { uri ->
            if (uri != null) {
                logoUri = uri.toString()
                logoEmoji = ""
                manualLogoSet = true
                showEmojiInput = false
            }
        },
        onVoiceResult = { }
    )

    val dynamicSubscriptionSuggestions = remember(existingExpenses) {
        val userSubs = existingExpenses
            .filter { !it.isDeleted && it.name.isNotBlank() && it.toAccountId == null }
            .map {
                PredefSubscription(
                    name = it.name.trim(),
                    suggestedCategory = "",
                    domain = null,
                    customUrl = it.logoUri,
                    emoji = it.logoEmoji
                )
            }
        (userSubs + defaultSubscriptionSuggestions).distinctBy { it.name.uppercase().trim() }
    }

    val currentCleanName = nameText.trim()
    val smartMatch = dynamicSubscriptionSuggestions.firstOrNull { it.name.equals(currentCleanName, ignoreCase = true) }
        ?: dynamicSubscriptionSuggestions.firstOrNull { currentCleanName.length >= 3 && it.name.contains(currentCleanName, ignoreCase = true) }

    val toAccountEntity = accounts.firstOrNull { it.id == toAccountId }

    // 🚀 FIX APPLICATO QUI: Se la modalità NON è transfer e c'è un logo manuale, prevale SEMPRE
    val finalEmojiToSave = when {
        isTransfer -> toAccountEntity?.logoEmoji
        manualLogoSet -> logoEmoji
        else -> smartMatch?.emoji
    }

    val finalUriToSave = when {
        isTransfer -> toAccountEntity?.logoUri
        manualLogoSet -> logoUri
        else -> smartMatch?.url ?: autoFaviconUrlForName(if (currentCleanName.isNotBlank()) currentCleanName else "RICORRENTE")
    }

    val canSave = remember(nameText, amountText, dayOfMonthText, accountId, isTransfer, toAccountId) {
        val amt = parseEuroToCents(amountText)
        val day = dayOfMonthText.toIntOrNull() ?: 0
        val baseOk = nameText.isNotBlank() && amt > 0L && day in 1..31 && !accountId.isNullOrBlank()
        if (isTransfer) baseOk && !toAccountId.isNullOrBlank() && accountId != toAccountId else baseOk
    }

    val onFabClick = {
        scope.launch {
            val dayInt = dayOfMonthText.toIntOrNull() ?: return@launch
            val cycleTargetInt = cycleTargetText.toLongOrNull()

            var finalCategoryId: String = initial?.categoryId ?: "default"
            if (!isTransfer) {
                val catTextClean = categorySearchText.trim().uppercase()
                if (catTextClean.isEmpty() || catTextClean == "NESSUNA CATEGORIA") {
                    finalCategoryId = "default"
                } else if (catTextClean != initialCategoryName.uppercase()) {
                    val existing = categories.firstOrNull { !it.isDeleted && it.name.equals(catTextClean, ignoreCase = true) }
                    if (existing != null) {
                        finalCategoryId = existing.id
                    } else {
                        val norm = catTextClean.lowercase().replace("\\s+".toRegex(), "_").replace("[^a-z0-9_]+".toRegex(), "")
                        val newId = "cat_$norm"
                        val newCat = CategoryEntity(newId, catTextClean, false, nowMs, nowMs)
                        db.categoryQueries.upsert(id = newId, name = catTextClean, isDeleted = false, createdAtEpochMs = nowMs, updatedAtEpochMs = nowMs)
                        onCategoryCreate?.invoke(newCat)
                        finalCategoryId = newId
                    }
                }
            } else {
                finalCategoryId = ""
            }

            val isDayChanged = initial == null || initial.dayOfMonth != dayInt.toLong()
            val finalNextEpoch = if (isDayChanged) {
                calcolaProssimaRicorrenza(dayInt).toEpochDays().toLong()
            } else {
                initial!!.nextEpochDay
            }

            val baseStartEpoch = initial?.startEpochDay ?: LocalDate(today.year, today.monthNumber, 1).toEpochDays().toLong()

            val preparedEntity = RecurringExpenseEntity(
                id = initial?.id ?: "rec_${nowMs}_${Random.nextInt(1000)}",
                name = currentCleanName,
                type = selectedType,
                amountCents = parseEuroToCents(amountText),
                accountId = accountId!!,
                toAccountId = if (isTransfer) toAccountId else null,
                categoryId = finalCategoryId,
                dayOfMonth = dayInt.toLong(),
                isEnabled = initial?.isEnabled ?: true,
                isDeleted = initial?.isDeleted ?: false,
                cycleTarget = cycleTargetInt,
                logoEmoji = finalEmojiToSave,
                logoUri = finalUriToSave,
                notes = notesText.trim().takeIf { it.isNotBlank() },
                autoGenerateOnReminder = autoGenerate,
                nextEpochDay = finalNextEpoch,
                frequency = if (frequencyType == "Personalizzato") {
                    val giorni = customDaysText.takeIf { it.isNotBlank() } ?: "30"
                    "CUSTOM_${giorni}_${if (skipWeekends) "SKIP" else "NOSKIP"}"
                } else {
                    "Mensile"
                },
                isPaused = initial?.isPaused ?: false,
                startEpochDay = baseStartEpoch,
                createdAtEpochMs = initial?.createdAtEpochMs ?: nowMs,
                updatedAtEpochMs = nowMs
            )

            if (dayInt < today.dayOfMonth) {
                pendingSaveEntity = preparedEntity
            } else {
                onSave(preparedEntity)
            }
        }
    }

    val pickerThemeColor = remember(selectedType, isTransfer) {
        if (isTransfer) Color(0xFF60A5FA)
        else if (selectedType == "INCOME") Color(0xFF10B981)
        else Color(0xFF60A5FA)
    }

    val isInnerPopupOpen = showAmountPicker || showWebSearch || pendingSaveEntity != null

    val dialogBlur by animateDpAsState(
        targetValue = if (isInnerPopupOpen) 12.dp else 0.dp,
        animationSpec = tween(durationMillis = 300),
        label = "dialogBlur"
    )

    val bgGradient = remember(selectedType, isTransfer) {
        val colors = if (isTransfer) {
            listOf(Color(0xFF0284C7).copy(alpha = 0.75f), Color(0xFF0C4A6E), Color(0xFF0F172A))
        } else if (selectedType == "INCOME") {
            listOf(Color(0xFF059669).copy(alpha = 0.75f), Color(0xFF064E3B), Color(0xFF0F172A))
        } else {
            listOf(Color(0xFF4F46E5).copy(alpha = 0.75f), Color(0xFF1E1B4B), Color(0xFF0F172A))
        }
        Brush.verticalGradient(colors = colors)
    }

    Dialog(
        onDismissRequest = onDismiss,
        properties = DialogProperties(
            usePlatformDefaultWidth = false,
            dismissOnClickOutside = true,
            dismissOnBackPress = true
        )
    ) {
        Surface(
            modifier = Modifier.fillMaxSize(),
            color = Color.Transparent
        ) {
            Box(modifier = Modifier.fillMaxSize().background(bgGradient).blur(dialogBlur)) {
                Column(
                    modifier = Modifier
                        .fillMaxSize()
                        .systemBarsPadding()
                        .imePadding()
                        .clickable(
                            interactionSource = remember { MutableInteractionSource() },
                            indication = null,
                            onClick = { categoryMenuOpen = false; merchantSuggestionsExpanded = false; accountExpanded = false; toAccountExpanded = false }
                        )
                ) {
                    // HEADER
                    Row(
                        modifier = Modifier.fillMaxWidth().padding(horizontal = 8.dp, vertical = 8.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        IconButton(onClick = onDismiss) {
                            Icon(Icons.Default.ArrowBack, contentDescription = "Back", tint = Color.White)
                        }
                    }

                    Text(
                        text = if (initial != null) "Modifica Spesa" else "Nuova Spesa Fissa",
                        style = MaterialTheme.typography.headlineMedium.copy(fontWeight = FontWeight.Bold),
                        color = Color.White,
                        modifier = Modifier.padding(horizontal = 24.dp)
                    )

                    Spacer(Modifier.height(24.dp))

                    // CORPO SCORREVOLE
                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .weight(1f)
                            .verticalScroll(rememberScrollState())
                            .padding(start = 24.dp, end = 24.dp, top = 0.dp, bottom = 120.dp),
                        verticalArrangement = Arrangement.spacedBy(16.dp)
                    ) {

                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .clip(RoundedCornerShape(14.dp))
                                .background(Color.White.copy(alpha = 0.10f))
                                .border(1.dp, Color.White.copy(alpha = 0.15f), RoundedCornerShape(14.dp))
                        ) {
                            TabBox("SPESA", !isTransfer && selectedType == "EXPENSE", Color(0xFF60A5FA)) {
                                isTransfer = false; selectedType = "EXPENSE"
                            }
                            TabBox("ENTRATA", !isTransfer && selectedType == "INCOME", Color(0xFF10B981)) {
                                isTransfer = false; selectedType = "INCOME"
                            }
                            TabBox("SPOSTA", isTransfer, Color(0xFF60A5FA)) {
                                isTransfer = true; selectedType = "TRANSFER"
                            }
                        }

                        // DETTAGLI E LOGO
                        PanelBox {
                            Text("DETTAGLI", style = MaterialTheme.typography.labelSmall, color = Color(0xFFA0A0A0))

                            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(16.dp)) {
                                val fallbackForLogo = if (isTransfer) toAccountEntity?.name ?: "TRASFERIMENTO" else if (currentCleanName.isNotBlank()) currentCleanName else "RICORRENTE"
                                TxLogo(
                                    logoEmoji = finalEmojiToSave,
                                    logoUri = finalUriToSave,
                                    name = fallbackForLogo,
                                    modifier = Modifier
                                        .size(54.dp)
                                        .border(1.dp, Color.White.copy(alpha = 0.1f), CircleShape)
                                )

                                if (!isTransfer) {
                                    FlowRow(
                                        modifier = Modifier.weight(1f),
                                        horizontalArrangement = Arrangement.spacedBy(6.dp),
                                        verticalArrangement = Arrangement.spacedBy(6.dp)
                                    ) {
                                        OutlinedButton(
                                            onClick = { mediaHandler.launchGallery() },
                                            modifier = Modifier.height(36.dp), shape = RoundedCornerShape(14.dp), border = BorderStroke(1.dp, Color.White.copy(alpha = 0.2f)), contentPadding = PaddingValues(horizontal = 12.dp)
                                        ) { Text("FOTO", style = MaterialTheme.typography.labelSmall, color = Color.White) }

                                        OutlinedButton(
                                            onClick = { showEmojiInput = !showEmojiInput; showWebSearch = false },
                                            modifier = Modifier.height(36.dp), shape = RoundedCornerShape(14.dp), border = BorderStroke(1.dp, Color.White.copy(alpha = 0.2f)), contentPadding = PaddingValues(horizontal = 12.dp)
                                        ) { Text("EMOJI", style = MaterialTheme.typography.labelSmall, color = Color.White) }

                                        OutlinedButton(
                                            onClick = { showWebSearch = true; showEmojiInput = false },
                                            modifier = Modifier.height(36.dp), shape = RoundedCornerShape(14.dp), border = BorderStroke(1.dp, Color.White.copy(alpha = 0.2f)), contentPadding = PaddingValues(horizontal = 12.dp)
                                        ) { Text("WEB", style = MaterialTheme.typography.labelSmall, color = Color.White) }
                                    }
                                }
                            }

                            AnimatedVisibility(visible = showEmojiInput && !isTransfer) {
                                StableWebTextField(
                                    value = logoEmoji,
                                    onValueChange = {
                                        if(it.length <= 3) {
                                            logoEmoji = it
                                            logoUri = ""
                                            manualLogoSet = true
                                        }
                                    },
                                    label = "Inserisci Emoji"
                                )
                            }

                            StableWebTextField(
                                value = amountText,
                                onValueChange = { amountText = it },
                                label = "IMPORTO (€)",
                                keyboardType = KeyboardType.Decimal,
                                interactionSource = amountInteractionSource,
                                trailingIcon = { IconButton(onClick = { showAmountPicker = true }) { Icon(Icons.Default.MoreVert, null, tint = Color.White) } }
                            )

                            Box(modifier = Modifier.fillMaxWidth()) {
                                StableWebTextField(
                                    value = nameText,
                                    onValueChange = {
                                        nameText = it
                                        merchantSuggestionsExpanded = it.isNotBlank() && !isTransfer
                                    },
                                    label = if (isTransfer) "NOME TRASFERIMENTO (es. PAC)" else "NOME SPESA (es. Netflix)",
                                    interactionSource = nameInteractionSource
                                )

                                if (!isTransfer) {
                                    val filteredMerchants = dynamicSubscriptionSuggestions.filter {
                                        it.name.contains(nameText, ignoreCase = true)
                                    }

                                    DropdownMenu(
                                        expanded = merchantSuggestionsExpanded && filteredMerchants.isNotEmpty() && isNameFocused,
                                        onDismissRequest = { merchantSuggestionsExpanded = false },
                                        properties = PopupProperties(focusable = false),
                                        modifier = Modifier.fillMaxWidth(0.8f).heightIn(max = 220.dp).background(Color(0xFF1C1C1E))
                                    ) {
                                        filteredMerchants.take(5).forEach { merch ->
                                            DropdownMenuItem(
                                                text = { Text(merch.name, fontWeight = FontWeight.SemiBold, color = Color.White) },
                                                leadingIcon = {
                                                    when {
                                                        merch.url != null -> AsyncImage(model = merch.url, contentDescription = null, modifier = Modifier.size(24.dp))
                                                        merch.emoji != null -> Text(merch.emoji, fontSize = 20.sp, color = Color.White)
                                                    }
                                                },
                                                onClick = {
                                                    nameText = merch.name
                                                    if (categorySearchText.isBlank()) categorySearchText = merch.suggestedCategory
                                                    logoUri = merch.url ?: ""
                                                    logoEmoji = merch.emoji ?: ""
                                                    manualLogoSet = false
                                                    merchantSuggestionsExpanded = false
                                                }
                                            )
                                        }
                                    }
                                }
                            }

                            StableWebTextField(
                                value = notesText,
                                onValueChange = { notesText = it },
                                label = "NOTE (Opzionale)"
                            )
                        }

                        AnimatedVisibility(visible = nameText.isBlank() && !isTransfer) {
                            Column {
                                Text("Spese frequenti:", style = MaterialTheme.typography.labelSmall, color = Color(0xFFA0A0A0), modifier = Modifier.padding(bottom = 8.dp))
                                LazyRow(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                                    items(dynamicSubscriptionSuggestions.take(8)) { merch ->
                                        Surface(
                                            color = Color.White.copy(alpha = 0.08f),
                                            shape = RoundedCornerShape(14.dp),
                                            border = BorderStroke(1.dp, Color.White.copy(alpha = 0.15f)),
                                            modifier = Modifier.clickable {
                                                nameText = merch.name
                                                if (categorySearchText.isBlank()) categorySearchText = merch.suggestedCategory
                                                logoUri = ""
                                                logoEmoji = ""
                                                manualLogoSet = false
                                            }
                                        ) {
                                            Row(
                                                verticalAlignment = Alignment.CenterVertically,
                                                modifier = Modifier.padding(horizontal = 12.dp, vertical = 8.dp),
                                                horizontalArrangement = Arrangement.spacedBy(8.dp)
                                            ) {
                                                when {
                                                    merch.url != null -> AsyncImage(model = merch.url, contentDescription = null, modifier = Modifier.size(16.dp))
                                                    merch.emoji != null -> Text(merch.emoji, fontSize = 14.sp, color = Color.White)
                                                }
                                                Text(merch.name, style = MaterialTheme.typography.bodySmall, color = Color.White)
                                            }
                                        }
                                    }
                                }
                            }
                        }

                        // PIANIFICAZIONE E ACCUMULO
                        PanelBox {
                            Text("PIANIFICAZIONE E ACCUMULO", style = MaterialTheme.typography.labelSmall, color = Color(0xFFA0A0A0))

                            StableWebNumberField(
                                value = dayOfMonthText,
                                onValueChange = { if (it.length <= 2) dayOfMonthText = it },
                                label = "GIORNO DEL MESE (1-31)",
                                modifier = Modifier.fillMaxWidth(),
                                interactionSource = dayInteractionSource
                            )

                            Column(modifier = Modifier.fillMaxWidth()) {
                                Text("Frequenza di rinnovo", style = MaterialTheme.typography.labelSmall, color = Color(0xFFA0A0A0))
                                Spacer(Modifier.height(8.dp))

                                Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                                    FilterChip(
                                        selected = frequencyType == "Mensile",
                                        onClick = { frequencyType = "Mensile" },
                                        label = { Text("Mensile", fontWeight = FontWeight.Bold) },
                                        shape = RoundedCornerShape(14.dp),
                                        colors = FilterChipDefaults.filterChipColors(
                                            selectedContainerColor = Color.White,
                                            selectedLabelColor = Color.Black,
                                            containerColor = Color.White.copy(alpha = 0.10f),
                                            labelColor = Color.White
                                        ),
                                        border = null
                                    )
                                    FilterChip(
                                        selected = frequencyType == "Personalizzato",
                                        onClick = { frequencyType = "Personalizzato" },
                                        label = { Text("Giorni esatti", fontWeight = FontWeight.Bold) },
                                        shape = RoundedCornerShape(14.dp),
                                        colors = FilterChipDefaults.filterChipColors(
                                            selectedContainerColor = Color.White,
                                            selectedLabelColor = Color.Black,
                                            containerColor = Color.White.copy(alpha = 0.10f),
                                            labelColor = Color.White
                                        ),
                                        border = null
                                    )
                                }

                                AnimatedVisibility(
                                    visible = frequencyType == "Personalizzato",
                                    enter = expandVertically() + fadeIn(),
                                    exit = shrinkVertically() + fadeOut()
                                ) {
                                    Column(modifier = Modifier.fillMaxWidth().padding(top = 12.dp), verticalArrangement = Arrangement.spacedBy(12.dp)) {
                                        MonetixTextField(
                                            value = customDaysText,
                                            onValueChange = { text -> if (text.all { it.isDigit() }) customDaysText = text },
                                            label = { Text("Ogni quanti giorni?", color = Color(0xFFA0A0A0)) },
                                            placeholder = { Text("Es. 28 o 30", color = Color(0xFFA0A0A0).copy(alpha = 0.5f)) },
                                            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                                            modifier = Modifier.fillMaxWidth(),
                                            shape = RoundedCornerShape(14.dp),
                                            singleLine = true
                                        )

                                        Surface(
                                            modifier = Modifier.fillMaxWidth().clip(RoundedCornerShape(14.dp)).clickable { skipWeekends = !skipWeekends },
                                            color = Color.White.copy(alpha = 0.08f),
                                            border = BorderStroke(1.dp, Color.White.copy(alpha = 0.15f))
                                        ) {
                                            Row(
                                                modifier = Modifier.padding(12.dp),
                                                verticalAlignment = Alignment.CenterVertically,
                                                horizontalArrangement = Arrangement.SpaceBetween
                                            ) {
                                                Column(modifier = Modifier.weight(1f).padding(end = 16.dp)) {
                                                    Text("Evita il weekend", style = MaterialTheme.typography.bodyMedium, fontWeight = FontWeight.Bold, color = Color.White)
                                                    Text("Se cade sabato/domenica, posticipa a lunedì", style = MaterialTheme.typography.bodySmall, color = Color(0xFFA0A0A0))
                                                }
                                                MonetixGlassSwitch(checked = skipWeekends, onCheckedChange = { skipWeekends = it }, activeColor = pickerThemeColor)
                                            }
                                        }
                                    }
                                }

                                if (savedNextDateStr != null) {
                                    Surface(
                                        modifier = Modifier.fillMaxWidth().padding(top = 12.dp),
                                        shape = RoundedCornerShape(14.dp),
                                        color = Color.White.copy(alpha = 0.08f),
                                        border = BorderStroke(1.dp, Color.White.copy(alpha = 0.15f))
                                    ) {
                                        Row(
                                            modifier = Modifier.padding(16.dp),
                                            horizontalArrangement = Arrangement.SpaceBetween,
                                            verticalAlignment = Alignment.CenterVertically
                                        ) {
                                            Text("Prossima scadenza:", style = MaterialTheme.typography.labelMedium, color = Color.White)
                                            Text(savedNextDateStr, style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.ExtraBold), color = pickerThemeColor)
                                        }
                                    }
                                }
                            }

                            if (!isTransfer) {
                                StableWebNumberField(
                                    value = cycleTargetText,
                                    onValueChange = { cycleTargetText = it },
                                    label = "Ciclo accumulo / Avviso (es. 6 mesi)",
                                    modifier = Modifier.fillMaxWidth(),
                                    interactionSource = cycleInteractionSource
                                )
                            }

                            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                                Column(modifier = Modifier.weight(1f).padding(end = 16.dp)) {
                                    Text("Generazione automatica", style = MaterialTheme.typography.labelMedium, color = Color.White)
                                    Text("Crea la transazione da sola nel giorno indicato", style = MaterialTheme.typography.bodySmall, color = Color(0xFFA0A0A0))
                                }
                                MonetixGlassSwitch(checked = autoGenerate, onCheckedChange = { autoGenerate = it }, activeColor = pickerThemeColor)
                            }
                        }

                        // TIPOLOGIA E CONTI
                        PanelBox {
                            Text("TIPOLOGIA E CONTI", style = MaterialTheme.typography.labelSmall, color = Color(0xFFA0A0A0))

                            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                                Column {
                                    Text("È un trasferimento?", style = MaterialTheme.typography.labelMedium, color = Color.White)
                                    Text("Sposta soldi tra i tuoi conti", style = MaterialTheme.typography.bodySmall, color = Color(0xFFA0A0A0))
                                }
                                MonetixGlassSwitch(checked = isTransfer, onCheckedChange = { isTransfer = it }, activeColor = pickerThemeColor)
                            }

                            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                                Text("Includi conti nascosti", style = MaterialTheme.typography.bodySmall, color = Color.White)
                                MonetixGlassSwitch(checked = showHiddenAccounts, onCheckedChange = { showHiddenAccounts = it }, activeColor = pickerThemeColor)
                            }

                            if (isTransfer) {
                                Column(modifier = Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(16.dp)) {
                                    Box(modifier = Modifier.fillMaxWidth()) {
                                        ExposedDropdownMenuBox(expanded = accountExpanded, onExpandedChange = { accountExpanded = !accountExpanded }) {
                                            val selName = stripSystemMarker(accounts.firstOrNull { it.id == accountId }?.name) ?: "DA CONTO"
                                            MonetixTextField(
                                                value = selName, onValueChange = {}, readOnly = true, label = { Text("DA CONTO", style = MaterialTheme.typography.labelSmall) },
                                                trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = accountExpanded) },
                                                modifier = Modifier.menuAnchor().fillMaxWidth(), shape = RoundedCornerShape(14.dp)
                                            )
                                            ExposedDropdownMenu(expanded = accountExpanded, onDismissRequest = { accountExpanded = false }, modifier = Modifier.background(Color(0xFF1C1C1E))) {
                                                accounts.filter { !it.isDeleted && (showHiddenAccounts || (it.isVisible && !it.isArchived) || it.id == accountId) }.forEach { a ->
                                                    DropdownMenuItem(text = { Text(stripSystemMarker(a.name) ?: a.name, color = Color.White) }, onClick = { accountId = a.id; accountExpanded = false })
                                                }
                                            }
                                        }
                                    }
                                    Box(modifier = Modifier.fillMaxWidth()) {
                                        ExposedDropdownMenuBox(expanded = toAccountExpanded, onExpandedChange = { toAccountExpanded = !toAccountExpanded }) {
                                            val curName = accounts.firstOrNull { it.id == toAccountId }?.name?.let { stripSystemMarker(it) ?: it } ?: "A CONTO"
                                            MonetixTextField(
                                                value = curName, onValueChange = {}, readOnly = true, label = { Text("A CONTO", style = MaterialTheme.typography.labelSmall) },
                                                trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = toAccountExpanded) },
                                                modifier = Modifier.menuAnchor().fillMaxWidth(), shape = RoundedCornerShape(14.dp)
                                            )
                                            ExposedDropdownMenu(expanded = toAccountExpanded, onDismissRequest = { toAccountExpanded = false }, modifier = Modifier.background(Color(0xFF1C1C1E))) {
                                                accounts.filter { !it.isDeleted && (showHiddenAccounts || (it.isVisible && !it.isArchived)) && it.id != accountId }.forEach { a ->
                                                    DropdownMenuItem(text = { Text(stripSystemMarker(a.name) ?: a.name, color = Color.White) }, onClick = { toAccountId = a.id; toAccountExpanded = false })
                                                }
                                            }
                                        }
                                    }
                                }
                            } else {
                                ExposedDropdownMenuBox(expanded = accountExpanded, onExpandedChange = { accountExpanded = !accountExpanded }) {
                                    val selName = stripSystemMarker(accounts.firstOrNull { it.id == accountId }?.name) ?: "SELEZIONA CONTO"
                                    MonetixTextField(
                                        value = selName, onValueChange = {}, readOnly = true, label = { Text("CONTO ADDEBITO", style = MaterialTheme.typography.labelSmall) },
                                        trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = accountExpanded) },
                                        modifier = Modifier.menuAnchor().fillMaxWidth(), shape = RoundedCornerShape(14.dp)
                                    )
                                    ExposedDropdownMenu(expanded = accountExpanded, onDismissRequest = { accountExpanded = false }, modifier = Modifier.background(Color(0xFF1C1C1E))) {
                                        accounts.filter { !it.isDeleted && (showHiddenAccounts || (it.isVisible && !it.isArchived) || it.id == accountId) }.forEach { a ->
                                            DropdownMenuItem(text = { Text(stripSystemMarker(a.name) ?: a.name, color = Color.White) }, onClick = { accountId = a.id; accountExpanded = false })
                                        }
                                    }
                                }

                                Box {
                                    StableWebTextField(
                                        value = categorySearchText,
                                        onValueChange = { categorySearchText = it; categoryMenuOpen = true },
                                        label = "CATEGORIA",
                                        interactionSource = catInteractionSource,
                                        trailingIcon = { IconButton(onClick = { categoryMenuOpen = !categoryMenuOpen }) { Icon(if (categoryMenuOpen) Icons.Default.KeyboardArrowUp else Icons.Default.KeyboardArrowDown, null, tint = Color.White) } }
                                    )

                                    DropdownMenu(
                                        expanded = categoryMenuOpen,
                                        onDismissRequest = { categoryMenuOpen = false },
                                        modifier = Modifier.fillMaxWidth(0.8f).heightIn(max = 220.dp).background(Color(0xFF1C1C1E)),
                                        properties = PopupProperties(focusable = false)
                                    ) {
                                        DropdownMenuItem(
                                            text = { Text("Nessuna categoria", color = Color.White) },
                                            colors = MenuDefaults.itemColors(textColor = Color.White),
                                            onClick = { categorySearchText = ""; categoryMenuOpen = false }
                                        )
                                        val filtered = categories.filter { !it.isDeleted && it.name.contains(categorySearchText, ignoreCase = true) }
                                        if (categorySearchText.isNotBlank() && filtered.none { it.name.equals(categorySearchText, ignoreCase = true) }) {
                                            DropdownMenuItem(
                                                text = { Text("+ Crea \"${categorySearchText.uppercase()}\"", color = pickerThemeColor, fontWeight = FontWeight.Bold) },
                                                colors = MenuDefaults.itemColors(textColor = pickerThemeColor),
                                                onClick = {
                                                    val catTextClean = categorySearchText.trim().uppercase()
                                                    val norm = catTextClean.lowercase().replace("\\s+".toRegex(), "_").replace("[^a-z0-9_]+".toRegex(), "")
                                                    val newId = "cat_$norm"
                                                    val newCat = CategoryEntity(newId, catTextClean, false, nowMs, nowMs)
                                                    scope.launch {
                                                        db.categoryQueries.upsert(id = newId, name = catTextClean, isDeleted = false, createdAtEpochMs = nowMs, updatedAtEpochMs = nowMs)
                                                        onCategoryCreate?.invoke(newCat)
                                                    }
                                                    categorySearchText = catTextClean; categoryMenuOpen = false
                                                }
                                            )
                                        }
                                        filtered.forEach { c ->
                                            DropdownMenuItem(
                                                text = { Text(c.name, color = Color.White) },
                                                colors = MenuDefaults.itemColors(textColor = Color.White),
                                                trailingIcon = {
                                                    IconButton(
                                                        onClick = {
                                                            scope.launch(Dispatchers.Default) {
                                                                db.categoryQueries.upsert(c.id, c.name, true, c.createdAtEpochMs, Clock.System.now().toEpochMilliseconds())
                                                            }
                                                            if (categorySearchText == c.name) categorySearchText = ""
                                                        }
                                                    ) {
                                                        Icon(Icons.Outlined.Delete, "Elimina", tint = MaterialTheme.colorScheme.error)
                                                    }
                                                },
                                                onClick = { categorySearchText = c.name; categoryMenuOpen = false }
                                            )
                                        }
                                    }
                                }
                            }
                        }

                        Spacer(Modifier.height(80.dp)) // Padding per il FAB
                    }
                }

                // FAB per salvare
                FloatingActionButton(
                    onClick = { if (canSave) onFabClick() },
                    containerColor = if (canSave) MaterialTheme.colorScheme.primary else Color.White.copy(alpha = 0.12f),
                    contentColor = if (canSave) Color.White else Color(0xFFA0A0A0),
                    modifier = Modifier
                        .align(Alignment.BottomEnd)
                        .navigationBarsPadding()
                        .padding(end = 24.dp, bottom = 64.dp)
                ) {
                    Icon(
                        Icons.Default.ArrowDownward,
                        contentDescription = "Salva",
                        tint = if (canSave) Color.White else Color(0xFFA0A0A0)
                    )
                }
            }
        }

        // 🚀 DIALOG PER LA SCELTA RETROATTIVA O FUTURA
        if (pendingSaveEntity != null) {
            val ent = pendingSaveEntity!!
            AlertDialog(
                onDismissRequest = { pendingSaveEntity = null },
                shape = RoundedCornerShape(24.dp),
                containerColor = Color(0xFF1C1C1E),
                title = { Text("Applica Modifiche", style = MaterialTheme.typography.titleMedium, color = brandPrimary, fontWeight = FontWeight.Bold) },
                text = { Text("La data di scadenza (Giorno ${ent.dayOfMonth}) è già passata questo mese.\n\nVuoi che l'app consideri questa modifica valida già per il mese in corso (verrà messa in accantonamento tra le tue uscite fisse) oppure vuoi farla partire direttamente dal prossimo mese?", style = MaterialTheme.typography.bodyMedium, color = Color.White) },
                confirmButton = {
                    Column(modifier = Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                        Button(
                            onClick = {
                                val dataForzata = calcolaProssimaRicorrenza(ent.dayOfMonth.toInt(), forzareMeseCorrente = true)
                                onSave(ent.copy(startEpochDay = dataForzata.toEpochDays().toLong(), nextEpochDay = dataForzata.toEpochDays().toLong()))
                                pendingSaveEntity = null
                            },
                            modifier = Modifier.fillMaxWidth()
                        ) {
                            Text("APPLICA PER QUESTO MESE", fontWeight = FontWeight.Bold)
                        }

                        OutlinedButton(
                            onClick = {
                                val nextOccurrence = calcolaProssimaRicorrenza(ent.dayOfMonth.toInt(), forzareMeseCorrente = false).toEpochDays().toLong()
                                onSave(ent.copy(startEpochDay = nextOccurrence, nextEpochDay = nextOccurrence))
                                pendingSaveEntity = null
                            },
                            modifier = Modifier.fillMaxWidth()
                        ) {
                            Text("INIZIA DAL PROSSIMO MESE", color = Color.White)
                        }

                        TextButton(
                            onClick = { pendingSaveEntity = null },
                            modifier = Modifier.fillMaxWidth()
                        ) {
                            Text("ANNULLA SALVATAGGIO", color = MaterialTheme.colorScheme.error)
                        }
                    }
                },
                dismissButton = {}
            )
        }

        if (showAmountPicker) {
            AmountWheelDialog(
                initialAmount = amountText,
                onDismiss = { showAmountPicker = false },
                onConfirm = { newAmount ->
                    amountText = newAmount
                    showAmountPicker = false
                }
            )
        }

        if (showWebSearch) {
            WebImagePickerPopup(
                initialQuery = nameText,
                onDismiss = { showWebSearch = false },
                onImageSelected = { url ->
                    logoUri = url
                    logoEmoji = ""
                    manualLogoSet = true
                    showWebSearch = false
                }
            )
        }
    }
}

fun calcolaProssimaRicorrenza(giornoInserito: Int, forzareMeseCorrente: Boolean = false): LocalDate {
    val oggi = Clock.System.todayIn(TimeZone.currentSystemDefault())
    val giornoSicuro = giornoInserito.coerceIn(1, 31)

    fun safeDate(year: Int, month: Int, day: Int): LocalDate {
        var d = day
        while (true) {
            try { return LocalDate(year, month, d) } catch (e: IllegalArgumentException) { d-- }
        }
    }

    if (forzareMeseCorrente) {
        return safeDate(oggi.year, oggi.monthNumber, giornoSicuro)
    }

    var prossimaData = safeDate(oggi.year, oggi.monthNumber, giornoSicuro)

    if (prossimaData < oggi) {
        var nextMonth = oggi.monthNumber + 1
        var nextYear = oggi.year
        if (nextMonth > 12) {
            nextMonth = 1
            nextYear++
        }
        prossimaData = safeDate(nextYear, nextMonth, giornoSicuro)
    }
    return prossimaData
}